import React, { useMemo, useState } from 'react'
import { Avatar, Badge, Button, SectionCard } from '@enamelai/design-system'
import { Icon as MembershipIcon } from '@enamelai/design-system/icons/Membership'
import {
  buildMembershipMemberDetail,
  MEMBERSHIP_STATUS_BADGE_COLOR,
  membershipTierLabel,
} from '@constants/Membership/membershipPage.constants'
import CancelMembershipModal from '@components/pages/Membership/CancelMembershipModal.component'
import TemporaryHoldModal from '@components/pages/Membership/TemporaryHoldModal.component'
import ReactivateMembershipModal from '@components/pages/Membership/ReactivateMembershipModal.component'
import ManageRecurringPaymentModal from '@components/pages/Membership/ManageRecurringPaymentModal.component'
import UpdateMembershipPlanModal from '@components/pages/Membership/UpdateMembershipPlanModal.component'
import type {
  MembershipPageMember,
  MembershipViewSource,
} from '@dataTypes/membership.type'
import type { Patient } from '@dataTypes/PatientInfo.types'
import './PatientProfileMembershipOverview.css'

type PatientProfileMembershipOverviewProps = {
  patient: Patient
  fullName: string
  member: MembershipViewSource
  onMemberUpdated: (member: MembershipViewSource) => void
}

type MembershipModal =
  | 'cancel'
  | 'hold'
  | 'reactivate'
  | 'payment'
  | 'update'
  | null

const OverviewField: React.FC<{ value: string; label: string }> = ({
  value,
  label,
}) => (
  <div className="profile-membership-overview__tile">
    <p className="profile-membership-overview__value">{value}</p>
    <p className="profile-membership-overview__label">{label}</p>
  </div>
)

const PatientProfileMembershipOverview: React.FC<
  PatientProfileMembershipOverviewProps
> = ({ patient, fullName, member, onMemberUpdated }) => {
  const detail = useMemo(() => buildMembershipMemberDetail(member), [member])
  const [openModal, setOpenModal] = useState<MembershipModal>(null)
  const [heldMemberId, setHeldMemberId] = useState<string | null>(null)
  const [holdStartedAt, setHoldStartedAt] = useState<Date | null>(null)
  const [paymentDetails, setPaymentDetails] = useState<{
    billingLabel: string
    autoPayMethod: string
  } | null>(null)

  const closeModal = (): void => setOpenModal(null)
  const onHold = detail.status === 'On hold' || heldMemberId === detail.id

  const handleMembershipCanceled = (updated: MembershipPageMember): void => {
    onMemberUpdated(updated)
  }

  const handleMembershipHeld = (updated: MembershipPageMember): void => {
    onMemberUpdated(updated)
    setHeldMemberId(updated.id)
    setHoldStartedAt(new Date())
  }

  const handleMembershipReactivated = (updated: MembershipPageMember): void => {
    onMemberUpdated(updated)
    setHeldMemberId(null)
    setHoldStartedAt(null)
  }

  const handlePaymentDetailsSaved = (details: {
    member: MembershipPageMember
    billingLabel: string
    autoPayMethod: string
  }): void => {
    onMemberUpdated(details.member)
    setPaymentDetails({
      billingLabel: details.billingLabel,
      autoPayMethod: details.autoPayMethod,
    })
  }

  return (
    <SectionCard
      title="Overview"
      className="patient-profile-section profile-membership-overview"
    >
      <div className="profile-membership-overview__rows">
        <div className="profile-membership-overview__row profile-membership-overview__row--identity">
          <div className="profile-membership-overview__identity">
            <Avatar
              src={patient.photo_file?.file_path ?? patient.photo ?? undefined}
              name={fullName}
              alt=""
              size={40}
              className="profile-membership-overview__avatar"
            />
            <div className="profile-membership-overview__identity-text">
              <p className="profile-membership-overview__name">{fullName}</p>
              <p className="profile-membership-overview__role">{detail.role}</p>
            </div>
          </div>
          <div className="profile-membership-overview__plan">
            <span className="profile-membership-overview__tier">
              <MembershipIcon
                aria-hidden="true"
                className={`profile-membership-overview__tier-icon profile-membership-overview__tier-icon--${detail.tier}`}
              />
              {membershipTierLabel(detail)} membership
            </span>
            <Badge
              mode="tinted"
              color={MEMBERSHIP_STATUS_BADGE_COLOR[detail.status]}
              label={onHold ? 'On hold' : detail.status}
            />
          </div>
        </div>
        <div className="profile-membership-overview__row">
          <OverviewField value={detail.memberNumber} label="Member ID" />
          <OverviewField value={detail.memberSinceLabel} label="Member since" />
        </div>
        <div className="profile-membership-overview__row">
          <OverviewField
            value={detail.nextPaymentDate}
            label={
              onHold
                ? 'Auto-payments will resume after reactivation.'
                : 'Next payment date'
            }
          />
          <OverviewField
            value={paymentDetails?.billingLabel ?? detail.billingLabel}
            label="Billing frequency"
          />
        </div>
        <div className="profile-membership-overview__row">
          <OverviewField
            value={paymentDetails?.autoPayMethod ?? detail.autoPayMethod}
            label="Auto-pay method"
          />
          <OverviewField
            value={detail.notificationChannels}
            label="Notification channels"
          />
        </div>
      </div>

      <div className="profile-membership-overview__actions">
        <Button
          variant="secondary"
          size="md"
          destructive
          onClick={() => setOpenModal('cancel')}
        >
          Cancel membership
        </Button>
        <div className="profile-membership-overview__actions-end">
          <Button
            variant="secondary"
            size="md"
            disabled={detail.disableEdit}
            onClick={() => setOpenModal('update')}
          >
            Edit membership
          </Button>
          {onHold ? (
            <Button
              variant="secondary"
              size="md"
              onClick={() => setOpenModal('reactivate')}
            >
              Reactivate
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="md"
              onClick={() => setOpenModal('hold')}
            >
              Temporary hold
            </Button>
          )}
          <Button
            variant="primary"
            size="md"
            onClick={() => setOpenModal('payment')}
          >
            Manage payments
          </Button>
        </div>
      </div>

      <CancelMembershipModal
        open={openModal === 'cancel'}
        member={detail}
        onClose={closeModal}
        onCanceled={handleMembershipCanceled}
      />
      <TemporaryHoldModal
        open={openModal === 'hold'}
        member={detail}
        onClose={closeModal}
        onHeld={handleMembershipHeld}
      />
      <ReactivateMembershipModal
        open={openModal === 'reactivate'}
        member={detail}
        holdStartedAt={holdStartedAt}
        onClose={closeModal}
        onReactivated={handleMembershipReactivated}
      />
      <ManageRecurringPaymentModal
        open={openModal === 'payment'}
        member={detail}
        onClose={closeModal}
        onSaved={handlePaymentDetailsSaved}
      />
      <UpdateMembershipPlanModal
        open={openModal === 'update'}
        member={member}
        onClose={closeModal}
        onUpdated={(updated) => {
          closeModal()
          if (updated) onMemberUpdated(updated)
        }}
      />
    </SectionCard>
  )
}

export default PatientProfileMembershipOverview
